import type { Patient } from '@medplum/fhirtypes'
import { formatDateNumber } from '@seen/core/dates'
import { featureFlags } from '@seen/core/featureFlags'
import { invariant } from '@seen/core/invariant'
import { ConditionClinicalStatus } from '@seen/core/models/conditions/constants'
import { Diagnosis } from '@seen/core/models/conditions/Diagnosis'
import { referenceFromReferenceString } from '@seen/core/references'
import { Badge, TypographyPageTitle } from '@seen/ui'
import type { ColumnDef, SortingState } from '@tanstack/react-table'
import { type JSX, useMemo, useState } from 'react'
import { useParams } from 'react-router'
import { Combobox } from '#src/components/core/combobox/Combobox.tsx'
import { DataTable } from '#src/components/core/data-table/DataTable.tsx'
import { LinkCell } from '#src/components/core/data-table/LinkCell.tsx'
import { ResourceFieldForm } from '#src/components/core/form/ResourceFieldForm.tsx'
import { Loader } from '#src/components/core/Loader.tsx'
import { useFeatureFlag } from '#src/hooks/useFeatureFlag.ts'
import { useOrderedConditionsList } from '#src/hooks/useOrderedConditions.ts'
import { useRafBreakdown } from '#src/hooks/useRafBreakdown.ts'
import { ParticipantRiskContributors } from '#src/pages/participants/conditions/ParticipantRiskContributors.tsx'
import { conditionSecondaryName } from '#src/utils/conditionSecondaryName.ts'

export function ParticipantConditionsPage(): JSX.Element {
  const { patientId } = useParams()
  invariant(patientId, 'Patient ID is required')
  return (
    <ParticipantConditionsPageComponent key={patientId} patientId={patientId} />
  )
}

function ParticipantConditionsPageComponent({
  patientId,
}: {
  patientId: string
}): JSX.Element {
  const patientReference = referenceFromReferenceString<Patient>(
    `Patient/${patientId}`
  )
  const facesheetImprovements = useFeatureFlag(
    featureFlags.facesheetImprovements
  )
  const {
    conditions,
    orderedConditions: currentConditions,
    isLoading,
  } = useOrderedConditionsList(patientReference, {
    manualOrder: !facesheetImprovements,
  })
  const { rafBreakdown, rafBreakdownV22, rafScore } = useRafBreakdown(patientId)

  const pastConditions = useMemo(
    () =>
      Diagnosis.sortForDisplay(
        (conditions ?? []).filter((condition) => !condition.isCurrent)
      ),
    [conditions]
  )

  return (
    <div className="flex flex-col gap-6">
      <TypographyPageTitle>Conditions</TypographyPageTitle>
      {isLoading && <Loader />}
      {conditions && (
        <ConditionsData
          currentConditions={currentConditions}
          pastConditions={pastConditions}
        />
      )}
      {(rafBreakdown || rafBreakdownV22) && (
        <ParticipantRiskContributors
          breakdown={rafBreakdown}
          breakdownV22={rafBreakdownV22}
          score={rafScore?.score}
          cmsScore={rafScore?.cmsScore}
        />
      )}
    </div>
  )
}

function ConditionsData({
  currentConditions,
  pastConditions,
}: {
  currentConditions: Diagnosis[]
  pastConditions: Diagnosis[]
}): JSX.Element {
  return (
    <div className="flex flex-col gap-8">
      <div className="flex flex-col gap-4">
        <h2>Current</h2>
        <ConditionsTable conditions={currentConditions} />
      </div>
      <div className="flex flex-col gap-4">
        <h2>Past</h2>
        <ConditionsTable conditions={pastConditions} />
      </div>
    </div>
  )
}

// Until a column header is clicked, rows keep the order they were given.
function ConditionsTable({
  conditions,
}: {
  conditions: Diagnosis[]
}): JSX.Element {
  const [sorting, setSorting] = useState<SortingState>([])
  return (
    <DataTable
      columns={columns}
      data={conditions}
      getRowId={(c) => c.id}
      enableSorting
      onSortingChange={setSorting}
      state={{ sorting }}
    />
  )
}

function ConditionNameCell({
  condition,
}: {
  condition: Diagnosis
}): JSX.Element {
  const secondaryName = conditionSecondaryName(condition)
  return (
    <LinkCell to={`./${condition.id}`}>
      {/* Single line: the table virtualizes past 100 rows against a fixed
          52px row-height estimate, so stacking a second line would push rows
          past the estimate and misalign the virtual scroll. */}
      <span className="flex min-w-0 items-baseline gap-1">
        <span className="shrink-0">{condition.name}</span>
        {secondaryName && (
          <span className="truncate text-foreground-tertiary">
            {secondaryName}
          </span>
        )}
      </span>
    </LinkCell>
  )
}

const columns: ColumnDef<Diagnosis>[] = [
  {
    accessorKey: 'name',
    header: 'Name',
    sortingFn: 'text',
    cell: ({ row }) => <ConditionNameCell condition={row.original} />,
  },
  {
    accessorKey: 'clinicalStatus',
    header: 'Status',
    sortingFn: 'text',
    cell: ({ row }) => {
      const condition = row.original
      return (
        <ResourceFieldForm<Diagnosis, ConditionClinicalStatus>
          instance={condition}
          valueFn={() => condition.clinicalStatus}
          patchFn={(instance, value) => {
            instance.clinicalStatus = value
          }}
          render={({ field: { value, onChange, ...fieldRest } }) => (
            <Combobox
              {...fieldRest}
              options={[
                {
                  label: 'Active',
                  value: ConditionClinicalStatus.ACTIVE,
                  keywords: ['active'],
                },
                {
                  label: 'Controlled',
                  value: ConditionClinicalStatus.CONTROLLED,
                  keywords: ['controlled'],
                },
                {
                  label: 'Resolved',
                  value: ConditionClinicalStatus.RESOLVED,
                  keywords: ['resolved'],
                },
              ]}
              onSelect={(value) => {
                onChange(value ?? ConditionClinicalStatus.ACTIVE)
              }}
            >
              <Badge className="cursor-pointer" variant="outline">
                {value}
              </Badge>
            </Combobox>
          )}
        />
      )
    },
  },
  {
    accessorKey: 'onsetDate',
    header: 'Onset',
    sortingFn: 'datetime',
    sortUndefined: 'last',
    cell: ({ row }) => (
      <LinkCell to={`./${row.original.id}`}>
        {row.original.onsetDateString
          ? formatDateNumber(row.original.onsetDateString)
          : null}
      </LinkCell>
    ),
  },
]
